/**
 * The value half of a cascading (Company > Building > Location) dropdown constraint.
 *
 * `foreignRelationSelectionOnForm` is the *metadata* half: authored in Blockly, it only says
 * which sibling relation this control listens to. The selected value is not known until
 * runtime, so `FilterInterdependencyBoundary` computes it and applies it through this filter.
 *
 * This used to be applied as a bare `viewCriteria.addFind({...})`. A bare find carries no
 * `bbFilterType`/`bbFilter`, so `getFiltersFromView` could not see it, and the Go Fetch stage-2
 * server page (#332) was therefore fetched WITHOUT the parent constraint and then used to gate
 * the view — a dropdown showed the intersection of "children of the selected parent" and "an
 * arbitrary alphabetical page of the whole relation", which is usually a fraction of the real
 * answer and frequently empty. Being a real filter type is what puts the constraint in the
 * server query and lets `onBeforeChange` reset the display gate when the parent changes.
 *
 * Shape: `{ propertyName: 'location:company._id', values: ['<id>', ...] }`.
 *
 * `getMql` accepts an array of those and `$and`s them, but that is defensive only — one view
 * cannot currently carry more than one. `getFiltersFromView` keys the filters it rebuilds by
 * `bbFilterType` (`viewCriteria.js`), so a second instance would overwrite the first and only one
 * would reach the server query, which is the same silent drop this filter exists to fix. Two
 * instances cannot arise today: `extractFiltersFromHNodes` throws `Filter type ... is already
 * defined` for a control with two `ForeignRelationSelectionOnForm` children (it is not on that
 * function's allow-list of repeatable types), and each control gets its own view. If that ever
 * changes, `getFiltersFromView` has to carry a list before the array branch here means anything.
 */
export default {
    getFilter,
    getMql,
    getUriComponent,
    pageResetRequired: true
};

/**
 * @param {string} propertyName fully qualified foreign key path, e.g. `location:company._id`
 * @param {string[]} values selected foreign record ids
 */
function getFilter(propertyName, values) {
    return { propertyName, values };
}

function getUriComponent(filters) {
    const criteria = getMql(filters);
    if (criteria == null) return;
    // `getQueryUrl` merges `criteria` from every filter into a single `criteria=` query
    // parameter. The server casts any key ending in `_id` (including inside `$in`) from a hex
    // string to an ObjectId — see lib/base-server idUtility.convertAllStringIdsToObjectIds.
    return { criteria };
}

function getMql(filters) {
    // We support having multiple instances of this filter; concat with an array to be sure
    // we have one.
    const definitions = [].concat(filters?.foreignRelationSelectionValue ?? []);
    const criteria = definitions.map(toCriteria).filter(x => x);

    if (criteria.length === 0) return;
    if (criteria.length === 1) return criteria[0];
    return { $and: criteria };
}

function toCriteria(definition) {
    const { propertyName, values } = definition ?? {};
    if (!propertyName || !Array.isArray(values) || values.length === 0) return;
    // A single selection uses equality rather than a one-element `$in` so the criteria keeps
    // the shape the raw find produced, both locally and on the wire.
    return { [propertyName]: values.length === 1 ? values[0] : { $in: values } };
}
